import { z } from 'zod';
import { AppLocale, APP_LOCALES, DEFAULT_LOCALE } from './locale.js';
/**
 * Runtime site configuration — GET /api/v1/config/public (PLAN.md §4.5).
 *
 * The SPA is ONE immutable bundle serving every domain, so nothing here may be
 * baked in at build time. Vite inlines `VITE_*` at build, which is exactly why
 * branding, locales and module flags arrive over the wire instead.
 */
/** Brand colours as space-separated RGB channels, e.g. "30 39 73". */
const RgbChannels = z
    .string()
    .regex(/^\d{1,3} \d{1,3} \d{1,3}$/, 'expected "R G B" channel triplet');
/**
 * Channels, not hex — Tailwind's `<alpha-value>` modifier needs them separated
 * so `bg-brand/50` works (PLAN.md §9.4).
 */
export const BrandTokens = z.object({
    navy: RgbChannels,
    navyDeep: RgbChannels,
    gold: RgbChannels,
    blue: RgbChannels,
});
/** KCM Trade brand guideline values (PLAN.md §9.1). */
export const DEFAULT_BRAND = {
    navy: '30 39 73', // #1E2749
    navyDeep: '18 26 45', // #121A2D
    gold: '245 197 0', // #F5C500
    blue: '57 70 117', // #394675
};
/** Funnel modules that can be toggled per site from the admin center (§7). */
export const ModuleKey = z.enum([
    'turnstile', // default ON (§18.2); effective only once keys are configured
    'phoneCapture',
    'poaUpload',
    'amlStep',
    'pwaPrompt',
]);
/**
 * One hero-slider banner. The image is a URL (hosted on the marketing CDN);
 * `linkUrl` makes the slide a click-through, `alt` is the accessible label.
 */
export const Banner = z.object({
    // A hosted URL or a compressed base64 data URL: the admin resizes and
    // re-encodes an uploaded image before sending, and the large bound holds the
    // resulting data URL.
    imageUrl: z.string().min(1).max(500_000),
    linkUrl: z.string().url().max(2048).optional(),
    alt: z.string().max(200).optional(),
});
export const PublicSiteConfig = z.object({
    /** Canonical host this config was resolved for. */
    host: z.string(),
    name: z.string(),
    logoUrl: z.string(),
    brand: BrandTokens,
    defaultLocale: AppLocale,
    // Drops any locale this build does not recognise, so a database seeded ahead
    // of a deploy (a new locale added before its code ships) degrades to not
    // offering that locale rather than failing the whole config and white-screening
    // the funnel.
    enabledLocales: z.preprocess((v) => {
        const known = (Array.isArray(v) ? v : []).filter((l) => APP_LOCALES.includes(l));
        return known.length > 0 ? known : [DEFAULT_LOCALE];
    }, z.array(AppLocale).min(1)),
    /** Only modules that are ON. Absence means off — the server enforces it too. */
    modules: z.array(ModuleKey),
    /** Public half of the Turnstile widget; null when the module is off. */
    turnstileSiteKey: z.string().nullable(),
    supportEmail: z.string(),
    legal: z.object({
        entityName: z.string(),
        cookiePolicyUrl: z.string(),
        privacyUrl: z.string(),
        /** The footer disclaimer. Optional so a row seeded before it still validates. */
        disclaimer: z.string().optional(),
    }),
    /** Hero slider images (§9). Absent/empty by default; served from `settings`. */
    banners: z.array(Banner).optional(),
    /**
     * Per-locale banner overrides (§9). A locale absent here — or present but
     * empty — falls back to `banners` (the English base), so a partner localises
     * only the languages they care to. Optional and additive: an older client
     * ignores it and shows the base set to everyone.
     */
    bannersByLocale: z.record(AppLocale, z.array(Banner)).optional(),
    /** Per-site analytics/tag HTML injected into the document head (§19). */
    customHead: z.string().optional(),
    /**
     * Tolgee runtime config for over-the-air translations (§17). A browser-
     * delivered client key, held in DB settings so it rotates in the admin
     * without a deploy; absent means the SPA uses only its bundled catalogs.
     */
    tolgee: z.object({ apiUrl: z.string(), apiKey: z.string() }).optional(),
});
/**
 * The banners to show for a locale: its own set when it has one, otherwise the
 * English base. Empty counts as "not set" — the operator's way of saying
 * "follow English" — so the rule holds in both storage and UI.
 */
export function resolveBanners(config, locale) {
    const own = config.bannersByLocale?.[locale];
    if (own && own.length > 0)
        return own;
    return config.banners ?? [];
}
//# sourceMappingURL=site.js.map